import axios from 'axios';
import api from './apiClient';
import { SIN_TOKEN } from '../embebido/puenteGdGo';

// =============================================================================
// El alta de propuestas de transmisión (#327, plan #323): la API de #324 (lectura) y #325 (escritura), en
// api/transmisiones/alta. El asesor sale siempre del token: ninguna llamada manda su id.
// =============================================================================

const BASE = '/api/transmisiones/alta';

export interface AsesorAlta {
  id: number;
  nombre?: string | null;
}

export interface PersonaAlta {
  id: number;
  codigo?: string | null;
  nombre?: string | null;
}

export interface PropuestaAlta {
  id: number;
  farmaciaId: number;
  farmacia?: string | null;
  provincia?: string | null;
  estadoId: number;
  /** yyyy-MM-dd */
  fecha: string;
  tramoFactId?: number | null;
  horarioId?: number | null;
  regimenLocalId?: number | null;
  precio?: number | null;
  factor?: number | null;
  enVentaExclusiva?: boolean | null;
  enVentaDesde?: string | null;
  enVentaHasta?: string | null;
  notas?: string | null;
  fechaAlta: string;
  fechaModificacion?: string | null;
  titulares: PersonaAlta[];
  nuevosTitulares: PersonaAlta[];
  empresaIds: number[];
  /** Si la farmacia se puede pasar a cartera (#328): no está bloqueada, en oportunidad, en cartera ni en fase 1, 2 o 3. */
  puedePasarACartera: boolean;
}

export interface FarmaciaAlta {
  id: number;
  nombre?: string | null;
  localidad?: string | null;
  provinciaId?: number | null;
  provincia?: string | null;
  tramoFactId?: number | null;
  horarioId?: number | null;
  regimenLocalId?: number | null;
  transmisionPendiente: boolean;
}

export interface FarmaciaAltaDetalle {
  farmacia: FarmaciaAlta;
  titulares: PersonaAlta[];
}

export interface ClienteAlta {
  id: number;
  codigo?: string | null;
  nombre?: string | null;
  poblacion?: string | null;
  provincia?: string | null;
}

export interface BusquedaClientesAlta {
  total: number;
  clientes: ClienteAlta[];
}

export interface OpcionAlta {
  id: number;
  nombre?: string | null;
}

export interface EmpresaAlta {
  id: number;
  nombre?: string | null;
  cif?: string | null;
}

export interface CatalogosAlta {
  tramos: OpcionAlta[];
  horarios: OpcionAlta[];
  regimenes: OpcionAlta[];
  estados: OpcionAlta[];
  provincias: OpcionAlta[];
  empresas: EmpresaAlta[];
}

/** Los datos de una propuesta al crearla o editarla. Los titulares actuales los pone el servidor. */
export interface DatosPropuestaAlta {
  farmaciaId: number;
  fecha?: string | null;
  tramoFactId?: number | null;
  horarioId?: number | null;
  regimenLocalId?: number | null;
  factor?: number | null;
  notas?: string | null;
  enVentaExclusiva?: boolean | null;
  enVentaDesde?: string | null;
  enVentaHasta?: string | null;
  nuevosTitulares: number[];
  empresaIds: number[];
}

/** 0: farmacia en venta · 1: transmisión realizada. */
export interface CrearPropuestaAlta extends DatosPropuestaAlta {
  estadoId: 0 | 1;
}

// --- Lecturas (#324) ---

export const leerAsesor = async (): Promise<AsesorAlta> => (await api.get<AsesorAlta>(`${BASE}/yo`)).data;

export const leerPropuestas = async (): Promise<PropuestaAlta[]> => (await api.get<PropuestaAlta[]>(`${BASE}/propuestas`)).data;

export const leerPropuesta = async (id: number): Promise<PropuestaAlta> => (await api.get<PropuestaAlta>(`${BASE}/propuestas/${id}`)).data;

export const leerFarmacias = async (provinciaId: number): Promise<FarmaciaAlta[]> =>
  (await api.get<FarmaciaAlta[]>(`${BASE}/farmacias`, { params: { provinciaId } })).data;

export const leerFarmacia = async (id: number): Promise<FarmaciaAltaDetalle> =>
  (await api.get<FarmaciaAltaDetalle>(`${BASE}/farmacias/${id}`)).data;

/** El nombre va en el cuerpo, nunca en la URL. */
export const buscarClientes = async (texto: string, pagina = 1, tamano = 30): Promise<BusquedaClientesAlta> =>
  (await api.post<BusquedaClientesAlta>(`${BASE}/clientes/buscar`, { texto, pagina, tamano })).data;

export const leerCatalogos = async (): Promise<CatalogosAlta> => (await api.get<CatalogosAlta>(`${BASE}/catalogos`)).data;

// --- Escrituras (#325) ---

export const crearPropuesta = async (datos: CrearPropuestaAlta): Promise<PropuestaAlta> =>
  (await api.post<PropuestaAlta>(`${BASE}/propuestas`, datos)).data;

export const editarPropuesta = async (id: number, datos: DatosPropuestaAlta): Promise<PropuestaAlta> =>
  (await api.put<PropuestaAlta>(`${BASE}/propuestas/${id}`, datos)).data;

export const marcarVendida = async (id: number): Promise<PropuestaAlta> =>
  (await api.post<PropuestaAlta>(`${BASE}/propuestas/${id}/vendida`)).data;

export const notificarPropuesta = async (id: number): Promise<PropuestaAlta> =>
  (await api.post<PropuestaAlta>(`${BASE}/propuestas/${id}/notificar`)).data;

export const borrarPropuesta = async (id: number): Promise<void> => {
  await api.delete(`${BASE}/propuestas/${id}`);
};

// --- Pasar a cartera (#328) ---

/** Los asesores que pueden recibir una farmacia en cartera. */
export const leerAsesoresCartera = async (): Promise<AsesorAlta[]> => (await api.get<AsesorAlta[]>(`${BASE}/asesores-cartera`)).data;

/** El mensaje del backend de Gescom y qué pasó con el aviso por email al asesor que la recibe (#329). */
export interface ResultadoPasoCartera {
  message: string;
  emailEncolado: boolean;
  avisoEmail: string;
}

/** Pasa a cartera la farmacia de la propuesta, para el asesor elegido o, sin él, para el del token. */
export const pasarACartera = async (id: number, asesorId?: number): Promise<ResultadoPasoCartera> =>
  (await api.post<ResultadoPasoCartera>(`${BASE}/propuestas/${id}/cartera`, { asesorId })).data;

// --- Errores ---

export interface ErrorAlta {
  mensaje: string;
  /** Un 400 de la API: el motivo de cada campo, con el nombre del campo del formulario (camelCase). */
  campos?: Record<string, string>;
  estado?: number;
}

const GENERICO = 'No se ha podido completar la operación. Inténtalo de nuevo en unos minutos.';

/**
 * Traduce un error de la API del alta. 400: un motivo por campo. 403: sin permiso o sin ficha de empleado. 404 y 409:
 * su mensaje, redactado para el asesor. Lo demás, un texto genérico sin detalles del servidor.
 */
export const errorAlta = (error: unknown): ErrorAlta => {
  // Embebida en GdGO! (#326): sin token del puente en 10 s.
  if (error instanceof Error && error.message === SIN_TOKEN) return { mensaje: SIN_TOKEN };
  if (axios.isAxiosError(error) && error.response) {
    const { status, data } = error.response as {
      status: number;
      data?: { message?: string; error?: string; title?: string; errors?: Record<string, string[]> };
    };
    if (status === 400 && data?.errors) {
      const campos = Object.fromEntries(
        Object.entries(data.errors).map(([campo, motivos]) => [campo.charAt(0).toLowerCase() + campo.slice(1), motivos.join(' ')]),
      );
      return { mensaje: 'Revisa los datos marcados.', campos, estado: status };
    }
    if (status === 403) return { mensaje: data?.error || 'No tienes permiso para usar el alta de transmisiones.', estado: status };
    // 502: el backend de Gescom rechazó o no contestó el paso a cartera, con su mensaje.
    if (status === 404 || status === 409 || status === 502) return { mensaje: data?.message || GENERICO, estado: status };
    return { mensaje: GENERICO, estado: status };
  }
  return { mensaje: GENERICO };
};
